import React, { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useOnboarding } from "../contexts/OnboardingContext";
import { useWsNavigate } from "../hooks/useWsNavigate";
import OnboardingChecklist from "../components/onboarding/OnboardingChecklist";
import OnboardingCompletionCard from "../components/onboarding/OnboardingCompletionCard";

// Dedicated onboarding route. Keeping the checklist off the Dashboard removes the
// flicker where dashboard content painted before `showOnboarding` resolved.
const Onboarding = () => {
  const { user } = useAuth();
  const { setPageTitle } = usePageTitle();
  const {
    loading,
    isViewer,
    dismissed,
    isComplete,
    showCompletionCelebration,
    ackCompletion,
  } = useOnboarding();
  const navigate = useWsNavigate();

  useEffect(() => {
    setPageTitle("Get started");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  // Leave onboarding once there's nothing to do here: viewers never onboard, a
  // user who dismissed chose "not now", and completion (minus the one-time
  // celebration) lands them on the dashboard.
  useEffect(() => {
    if (loading) return;
    const doneHere =
      isViewer || dismissed || (isComplete && !showCompletionCelebration);
    if (doneHere) navigate("/dashboard", { replace: true });
  }, [loading, isViewer, dismissed, isComplete, showCompletionCelebration, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (showCompletionCelebration) {
    return (
      <OnboardingCompletionCard
        onAck={() => {
          ackCompletion();
          navigate("/dashboard", { replace: true });
        }}
      />
    );
  }

  // Redirecting (viewer / dismissed / complete) — render nothing this frame.
  if (isViewer || dismissed || isComplete) return null;

  return (
    <div className="px-4">
      <OnboardingChecklist firstName={user?.name?.split(" ")[0]} />
    </div>
  );
};

export default Onboarding;
